import { useEffect, useMemo } from "react";

/**
 * UTM tracking hook — captures UTM parameters from the URL query string
 * on first visit and persists them in sessionStorage so they survive
 * page navigation within the same session.
 *
 * Also captures the landing page URL and document referrer.
 *
 * Usage:
 *   const utm = useUTM();
 *   // utm.utmSource, utm.utmMedium, utm.utmCampaign, etc.
 */

export interface UTMData {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  landingPage: string;
  referrerUrl: string;
}

const UTM_STORAGE_KEY = "smiths_utm_data";

const UTM_PARAMS = [
  { key: "utm_source", field: "utmSource" },
  { key: "utm_medium", field: "utmMedium" },
  { key: "utm_campaign", field: "utmCampaign" },
  { key: "utm_term", field: "utmTerm" },
  { key: "utm_content", field: "utmContent" },
] as const;

function captureUTMFromURL(): UTMData | null {
  if (typeof window === "undefined") return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const hasUTM = UTM_PARAMS.some(({ key }) => params.has(key));

    if (!hasUTM) return null;

    const data: UTMData = {
      utmSource: params.get("utm_source") || "",
      utmMedium: params.get("utm_medium") || "",
      utmCampaign: params.get("utm_campaign") || "",
      utmTerm: params.get("utm_term") || "",
      utmContent: params.get("utm_content") || "",
      landingPage: window.location.pathname + window.location.search,
      referrerUrl: document.referrer || "",
    };

    return data;
  } catch {
    return null;
  }
}

function getStoredUTM(): UTMData | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as UTMData;
  } catch {
    return null;
  }
}

function storeUTM(data: UTMData): void {
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage might be unavailable in some contexts
  }
}

/**
 * Returns the current UTM data. On first page load with UTM params in the URL,
 * captures and stores them. On subsequent navigations, returns the stored values.
 * If no UTM data exists, returns empty strings (still captures landing page & referrer).
 */
export function useUTM(): UTMData {
  useEffect(() => {
    // Only capture on first visit (don't overwrite existing session data)
    const existing = getStoredUTM();
    if (existing) return;

    const fromURL = captureUTMFromURL();
    if (fromURL) {
      storeUTM(fromURL);
    } else {
      // Even without UTM params, store landing page and referrer
      const fallback: UTMData = {
        utmSource: "",
        utmMedium: "",
        utmCampaign: "",
        utmTerm: "",
        utmContent: "",
        landingPage: window.location.pathname + window.location.search,
        referrerUrl: document.referrer || "",
      };
      storeUTM(fallback);
    }
  }, []);

  return useMemo(() => {
    const stored = getStoredUTM();
    if (stored) return stored;

    // Fallback for SSR or first render before useEffect runs
    return {
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      utmTerm: "",
      utmContent: "",
      landingPage: typeof window !== "undefined" ? window.location.pathname : "",
      referrerUrl: typeof window !== "undefined" ? document.referrer : "",
    };
  }, []);
}

/**
 * Standalone function to get UTM data without a hook (for use in event handlers).
 * Reads from sessionStorage directly.
 */
export function getUTMData(): UTMData {
  const stored = getStoredUTM();
  if (stored) return stored;

  return {
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
    utmTerm: "",
    utmContent: "",
    landingPage: typeof window !== "undefined" ? window.location.pathname : "",
    referrerUrl: typeof window !== "undefined" ? document.referrer : "",
  };
}
